/*eslint import/no-unresolved: [2, { ignore: ['\\.lib_am_common/Reducers$'] }]*/
import * as AM_COMMON from '@innovyze/lib_am_common/Reducers';

import { NamespacedStoreState } from '@Types/store.types';
import { createSelector } from '@reduxjs/toolkit';
import {
	mapRowToCells,
	mapRowToCellsNoCD,
} from '@Reducers/riskDetailsPagination';
import { moduleNamespace } from '@Store/config.store';
import { useSelector } from 'react-redux';
import { noConditionData } from '@Components/RiskDetails/RiskDetails.component';
import { ComponentCountReply } from '@Types/risk.types';
import { GridRowId } from '@innovyze/stylovyze';
import { RiskDetailsRow } from '@Types/riskConfig.types';
import { FilterDict } from '@innovyze/lib_am_common';

const parseNamespace = (state: NamespacedStoreState) => state[moduleNamespace];

const riskStoreSelector = createSelector(
	parseNamespace,
	state => state.RiskStore,
);

export const RiskConfigSelector = createSelector(
	riskStoreSelector,
	RiskStore => RiskStore.riskConfig,
);

const RiskSettingSelector = createSelector(
	riskStoreSelector,
	RiskStore => RiskStore.riskConfig.riskSetting,
);

const RiskTabSelector = createSelector(
	riskStoreSelector,
	riskStore => riskStore.tab,
);

const cofCategoriesSelector = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskConfig.COFCategories,
);

const lofCategoriesSelector = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskConfig.LOFCategories,
);

export const selectorRiskDetailsPagination = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskDetails?.pagination,
);

export const selectorRiskDetailsRows = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskDetails?.rows as unknown as RiskDetailsRow[],
);

const selectorPaginationQuery = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskDetails.query,
);

const selectorFilterQuery = createSelector(
	riskStoreSelector,
	riskStore => riskStore.dropdownFilters.query,
);

const selectorIsRiskSetupDirty = createSelector(
	riskStoreSelector,
	riskStore => riskStore.isSetupDirty,
);

export const searchQuerySelector = createSelector(
	riskStoreSelector,
	riskStore =>
		riskStore.search.length
			? `&search=${encodeURIComponent(riskStore.search)}`
			: '',
);

export const riskDetailsQuerySelector = createSelector(
	riskStoreSelector,
	selectorPaginationQuery,
	selectorFilterQuery,
	searchQuerySelector,
	(riskStore, pag, filters, search) => {
		const settings = riskStore.riskConfig.riskSetting;
		const riskDetails = riskStore.riskDetails;
		let q = pag;
		q = q.length
			? q + `&configId=${settings.configId}`
			: `?configId=${settings.configId}`;
		if (riskDetails.scanId) {
			q = q.length
				? q + `&scanId=${riskDetails.scanId}`
				: `?scanId=${riskDetails.scanId}`;
		}
		if (filters.length) {
			q += filters;
		}
		if (search.length) {
			q += search;
		}
		q = q + '&includeScores=true';
		return q;
	},
);

export const riskDetailsQuerySelectorObj = createSelector(
	riskStoreSelector,
	selectorPaginationQuery,
	selectorFilterQuery,
	searchQuerySelector,
	(riskStore, pag, filters, search) => {
		const settings = riskStore.riskConfig.riskSetting;
		const riskDetails = riskStore.riskDetails;
		const obj: {
			[key: string]: string | FilterDict | boolean | undefined;
		} = {};
		if (pag?.length) {
			obj['sort'] = riskStore.riskDetails?.pagination?.sortKey;
			obj['sortDescending'] =
				riskStore.riskDetails?.pagination?.sortDescending ?? false;
		}
		obj['configId'] = settings.configId;
		if (riskDetails.scanId) {
			obj['scanId'] = riskDetails.scanId;
		}
		if (filters.length) {
			obj['filters'] = riskStore.dropdownFilters.filters;
		}
		if (search.length) {
			obj['search'] = encodeURIComponent(riskStore.search);
		}
		obj['includeScores'] = true;
		return obj;
	},
);

const selectorRiskDetailsTablePage = createSelector(
	[
		selectorRiskDetailsPagination,
		selectorRiskDetailsRows,
		RiskConfigSelector,
	],
	(pagination, rows, config) => ({
		pages: pagination.pages,
		page: pagination.page,
		cells: rows.map(r =>
			noConditionData(config.riskSetting.assetType)
				? mapRowToCellsNoCD(r)
				: mapRowToCells(r),
		),
		rowIds: rows.map(r => r?._id || ''),
		sortKey: pagination.sortKey,
		sortDescending: pagination.sortDescending,
		total: pagination.total,
	}),
);

const selectorCanReload = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskDetails?.pagination?.total > 0,
);

const selectorRunCompleteReload = createSelector(
	riskStoreSelector,
	riskStore => riskStore.runCompleteReload,
);

const selectorRiskDetailsSlice = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskDetails.pagination.slice,
);

const selectorRiskDetailsIsLoading = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskDetails.isLoading,
);
const selectorIsLoading = createSelector(
	riskStoreSelector,
	riskStore => riskStore.isLoading,
);

const selectorRiskDetailsIsWaiting = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskDetails.isWaiting,
);

const selectorSaveRunSettings = createSelector(
	riskStoreSelector,
	riskStore => riskStore.saveRun,
);

const selectorRiskDetailsSearch = createSelector(
	riskStoreSelector,
	riskStore => riskStore.search,
);

const selectorRiskConfigNameError = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskConfigNameError,
);

const selectorRiskConfigIsCreating = createSelector(
	riskStoreSelector,
	riskStore => riskStore.isCreating,
);

const selectorRiskConfigIsSaving = createSelector(
	riskStoreSelector,
	riskStore => riskStore.isSaving,
);

const selectorRiskIsDeleting = createSelector(
	riskStoreSelector,
	riskStore => riskStore.isDeleting,
);

const riskDetailsStatusSelector = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskDetails.status,
);

const selectorRefreshList = createSelector(
	riskStoreSelector,
	riskStore => riskStore.refreshList,
);

const RiskIsCountLoadingStoreSelector = createSelector(
	riskStoreSelector,
	riskStore => riskStore.isCountLoading,
);

const RiskCountStoreSelector = createSelector(
	riskStoreSelector,
	state => state.riskCount,
);

const selectorFilterProps = createSelector(riskStoreSelector, riskStore =>
	Object.values(riskStore.dropdownFilters.selects),
);

const passInSelection = (
	state: NamespacedStoreState,
	selectedItems: GridRowId[],
): GridRowId[] => selectedItems;

export const selectSelectionGridRows = createSelector(
	[selectorRiskDetailsRows, passInSelection],
	(rows, selection) => rows?.filter(({ _id }) => selection.includes(_id)),
);

export const selectSelectedRiskRows = createSelector(
	[selectSelectionGridRows],
	rows =>
		rows.map(({ _id }) => ({
			_id,
		})),
);

export const selectCanReload = () => useSelector(selectorCanReload);

export const selectorRowsPerPage = (dispatchSliceFn: (slice: number) => void) =>
	createSelector(selectorRiskDetailsSlice, slice =>
		AM_COMMON.rowsPerPage(slice || 10, dispatchSliceFn),
	);
export const selectRunCompleteReload = () =>
	useSelector(selectorRunCompleteReload);

export const selectRefreshList = () => useSelector(selectorRefreshList);

export const selectRiskStore = () => useSelector(riskStoreSelector);

export const selectRiskConfig = () => useSelector(RiskConfigSelector);

export const selectRiskSetting = () => useSelector(RiskSettingSelector);

export const selectRiskTab = () => useSelector(RiskTabSelector);

export const selectRiskDetailsQuery = () =>
	useSelector(riskDetailsQuerySelector);

export const selectRiskDetailsQueryObj = () =>
	useSelector(riskDetailsQuerySelectorObj);

export const selectRiskDetailsTablePage = () =>
	useSelector(selectorRiskDetailsTablePage);

export const selectRiskDetailsPagination = () =>
	useSelector(selectorRiskDetailsPagination);

export const selectRowsPerPage = (dispatchSliceFn: (slice: number) => void) =>
	useSelector(selectorRowsPerPage(dispatchSliceFn));
export const selectRiskDetailsIsLoading = () =>
	useSelector(selectorRiskDetailsIsLoading);
export const selectIsLoading = () => useSelector(selectorIsLoading);
export const selectRiskDetailsIsWaiting = () =>
	useSelector(selectorRiskDetailsIsWaiting);

export const selectSaveRunSettings = () => useSelector(selectorSaveRunSettings);

export const selectRiskDetailsSearch = () =>
	useSelector(selectorRiskDetailsSearch);
export const selectRiskDetailsFilterProps = () =>
	useSelector(selectorFilterProps);
export const selectIsRiskSetupDirty = () =>
	useSelector(selectorIsRiskSetupDirty);
export const selectRiskConfigNameError = () =>
	useSelector(selectorRiskConfigNameError);
export const selectRiskConfigIsCreating = () =>
	useSelector(selectorRiskConfigIsCreating);
export const selectRiskIsSaving = () => useSelector(selectorRiskConfigIsSaving);
export const selectRiskIsDeleting = () => useSelector(selectorRiskIsDeleting);

export const selectRiskDetailsStatus = () =>
	useSelector(riskDetailsStatusSelector);

export const selectorIsCountLoading = (key: string) => {
	return createSelector(RiskIsCountLoadingStoreSelector, state => {
		return state.indexOf(key) !== -1;
	});
};

export const useSelectIsCountLoading = (key: string): boolean =>
	useSelector(selectorIsCountLoading(key));

const selectorRiskCount = (key: string) => {
	return createSelector(RiskCountStoreSelector, state => {
		if (state) {
			return state[key];
		}
	});
};

export const selectRiskCount = (
	key: string,
): ComponentCountReply[] | undefined => useSelector(selectorRiskCount(key));

//=====risk matrix selectors
const selectorRiskMatrix = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskMatrix,
);
export const selectRiskMatrix = () => useSelector(selectorRiskMatrix);

const selectorDidMatrixUpdate = createSelector(
	riskStoreSelector,
	riskStore => riskStore.didMatrixUpdate,
);
export const selectDidMatrixUpdate = () => useSelector(selectorDidMatrixUpdate);

const selectorEnablePreview = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskType === 'matrix' && riskStore.enablePreview,
);
export const selectEnablePreview = () => useSelector(selectorEnablePreview);
const selectorPreviewCancelled = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskType === 'matrix' && riskStore.cancelPreview,
);
export const selectPreviewCancelled = () =>
	useSelector(selectorPreviewCancelled);

const selectorIsPreviewLoading = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskType === 'matrix' && riskStore.isPreviewLoading,
);
export const selectIsPreviewLoading = () =>
	useSelector(selectorIsPreviewLoading);

const selectorIsPreviewOnLoad = createSelector(
	riskStoreSelector,
	riskStore => riskStore.isPreviewOnLoad,
);
export const selectIsPreviewOnLoad = () => useSelector(selectorIsPreviewOnLoad);

const selectorOnlyGradesUpdated = createSelector(
	riskStoreSelector,
	riskStore => riskStore.isOnlyGradesUpdated,
);
export const selectOnlyGradesUpdated = () =>
	useSelector(selectorOnlyGradesUpdated);

const selectorUpdatedRiskRanges = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskMatrix.grades,
);
export const selectUpdatedRiskRanges = () =>
	useSelector(selectorUpdatedRiskRanges);

const selectorUpdatedRiskMatrixGrid = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskMatrix.grid,
);
export const selectUpdatedRiskMatrixGrid = () =>
	useSelector(selectorUpdatedRiskMatrixGrid);

const selectorRiskType = createSelector(
	riskStoreSelector,
	riskStore => riskStore.riskConfig.riskSetting.riskType,
);
export const selectRiskType = () => useSelector(selectorRiskType);

const selectorPreviewStatus = createSelector(
	riskStoreSelector,
	riskStore => riskStore.previewStatus,
);
export const selectPreviewStatus = () => useSelector(selectorPreviewStatus);

export const selectorRefreshMatrixGrid = createSelector(
	riskStoreSelector,
	riskStore => riskStore.refreshMatrixGrid,
);
export const selectRefreshMatrixGrid = () =>
	useSelector(selectorRefreshMatrixGrid);

export const selectorEditedCellsList = createSelector(
	riskStoreSelector,
	riskStore => riskStore.editedCellsList,
);
export const selectEditedCellsList = () => useSelector(selectorEditedCellsList);

export const selectorOverallRisk = createSelector(
	riskStoreSelector,
	riskStore => riskStore.overallRisk,
);
export const selectOverallRisk = () => useSelector(selectorOverallRisk);

export const useSelectLofCategories = () => useSelector(lofCategoriesSelector);
export const useSelectCofCategories = () => useSelector(cofCategoriesSelector);

///============
